import { Avatar } from "../components/Avatar";
import { Button } from "../components/Button";
import { Checkbox } from "../components/Checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, } from "../components/Dropdown";
import { Icon } from "../components/Icon";
import { IconButton } from "../components/IconButton";
import { Pagination } from "../components/Pagination";
import { radioIndicatorStyles, radioStyles, } from "../components/RadioGroup";
import { ScrollArea, ScrollBar } from "../components/ScrollArea";
import { Spinner } from "../components/Spinner";
import { Tooltip } from "../components/Tooltip";
import { useCopyToClipboard } from "../hooks";
import { ArrowDownIcon, ArrowUpIcon, ClipboardCheckIcon, ClipboardIcon, MoreIcon, } from "../icons/app";
import { cn } from "../lib/utils";
import { flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useEffect, useRef, useState } from "react";
import { breakpoints, useWindowSize } from "./WindowUtility";
const cellHeight = "s-h-12";
function shouldRenderColumn(windowWidth, breakpoint) {
    if (!breakpoint) {
        return true;
    }
    return windowWidth >= breakpoints[breakpoint];
}
export function DataTable({ data, totalRowCount, rowCountIsCapped = false, columns, className, widthClassName = "s-w-full", filter, filterColumn, columnsBreakpoints = {}, pagination, setPagination, sorting, setSorting, isServerSideSorting = false, disablePaginationNumbers = false, rowSelection, setRowSelection, enableRowSelection = false, enableMultiRowSelection = true, getRowId, enableSortingRemoval = true, }) {
    const windowSize = useWindowSize();
    const [columnFilters, setColumnFilters] = useState([]);
    const isServerSidePagination = !!totalRowCount && totalRowCount > data.length;
    const isClientSideSortingEnabled = !isServerSideSorting && !isServerSidePagination;
    const onPaginationChange = pagination && setPagination
        ? (updater) => {
            const newValue = typeof updater === "function" ? updater(pagination) : updater;
            setPagination(newValue);
        }
        : undefined;
    const onSortingChange = sorting && setSorting
        ? (updater) => {
            const newValue = typeof updater === "function" ? updater(sorting) : updater;
            setSorting(newValue);
        }
        : undefined;
    const onRowSelectionChange = rowSelection && setRowSelection
        ? (updater) => {
            const newValue = typeof updater === "function" ? updater(rowSelection) : updater;
            setRowSelection(newValue);
        }
        : undefined;
    const table = useReactTable({
        data,
        columns,
        rowCount: totalRowCount,
        manualPagination: isServerSidePagination,
        manualSorting: isServerSideSorting,
        ...(isServerSideSorting && {
            onSortingChange: onSortingChange,
        }),
        enableSortingRemoval,
        getCoreRowModel: getCoreRowModel(),
        ...(!isServerSideSorting && {
            getSortedRowModel: getSortedRowModel(),
            enableSorting: isClientSideSortingEnabled,
        }),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
        onColumnFiltersChange: setColumnFilters,
        ...(enableRowSelection && {
            onRowSelectionChange,
        }),
        state: {
            columnFilters,
            ...(isServerSideSorting && {
                sorting,
            }),
            pagination,
            ...(enableRowSelection && { rowSelection }),
        },
        initialState: {
            sorting,
        },
        onPaginationChange,
        enableRowSelection,
        enableMultiRowSelection,
        getRowId,
    });
    useEffect(() => {
        var _a;
        if (filterColumn) {
            (_a = table.getColumn(filterColumn)) === null || _a === void 0 ? void 0 : _a.setFilterValue(filter);
        }
    }, [filter, filterColumn]);
    return (React.createElement("div", { className: cn("s-flex s-flex-col s-gap-2", className, widthClassName) },
        React.createElement(DataTable.Root, null,
            React.createElement(DataTable.Header, null, table.getHeaderGroups().map((headerGroup) => (React.createElement(DataTable.Row, { key: headerGroup.id, widthClassName: widthClassName }, headerGroup.headers.map((header) => {
                const breakpoint = columnsBreakpoints[header.id];
                if (!windowSize.width ||
                    !shouldRenderColumn(windowSize.width, breakpoint)) {
                    return null;
                }
                return (React.createElement(DataTable.Head, { column: header.column, key: header.id, onClick: header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined, className: cn(header.column.getCanSort() && "s-cursor-pointer") },
                    React.createElement("div", { className: "s-flex s-items-center s-space-x-1 s-whitespace-nowrap" },
                        flexRender(header.column.columnDef.header, header.getContext()),
                        header.column.getCanSort() && (React.createElement(Icon, { visual: header.column.getIsSorted() === "asc"
                                ? ArrowUpIcon
                                : ArrowDownIcon, size: "xs", className: cn("s-ml-1", header.column.getIsSorted()
                                ? "s-opacity-100"
                                : "s-opacity-0") })))));
            }))))),
            React.createElement(DataTable.Body, null, table.getRowModel().rows.map((row) => {
                const handleRowClick = () => {
                    var _a, _b;
                    if (enableRowSelection && row.getCanSelect()) {
                        row.toggleSelected(!enableMultiRowSelection ? true : undefined);
                    }
                    (_b = (_a = row.original).onClick) === null || _b === void 0 ? void 0 : _b.call(_a);
                };
                return (React.createElement(DataTable.Row, { widthClassName: widthClassName, key: row.id, onClick: enableRowSelection ? handleRowClick : row.original.onClick, onDoubleClick: row.original.onDoubleClick, rowData: row.original, ...(enableRowSelection && {
                        "data-selected": row.getIsSelected(),
                    }) }, row.getVisibleCells().map((cell) => {
                    const breakpoint = columnsBreakpoints[cell.column.id];
                    if (!windowSize.width ||
                        !shouldRenderColumn(windowSize.width, breakpoint)) {
                        return null;
                    }
                    return (React.createElement(DataTable.Cell, { column: cell.column, key: cell.id }, flexRender(cell.column.columnDef.cell, cell.getContext())));
                })));
            }))),
        pagination && (React.createElement("div", { className: "s-p-1" },
            React.createElement(Pagination, { size: "xs", pagination: table.getState().pagination, setPagination: table.setPagination, rowCount: table.getRowCount(), rowCountIsCapped: rowCountIsCapped, disablePaginationNumbers: disablePaginationNumbers })))));
}
// cellHeight in pixels
const COLUMN_HEIGHT = 48;
const MIN_COLUMN_WIDTH = 40;
export function ScrollableDataTable({ data, totalRowCount, columns, className, widthClassName = "s-w-full", columnsBreakpoints = {}, maxHeight, onLoadMore, sorting, setSorting, isLoading = false, rowSelection, setRowSelection, enableRowSelection, enableMultiRowSelection = true, getRowId, containerRef, }) {
    const windowSize = useWindowSize();
    const loadMoreRef = useRef(null);
    const [tableWidth, setTableWidth] = useState(0);
    const tableContainerRef = useRef(null);
    const isSorting = !!setSorting;
    // Handle container ref
    const setRef = (element) => {
        tableContainerRef.current = element;
        if (containerRef) {
            if (typeof containerRef === "function") {
                containerRef(element);
            }
            else if ("current" in containerRef) {
                containerRef.current = element;
            }
        }
    };
    // Monitor table width changes
    useEffect(() => {
        if (!tableContainerRef.current) {
            return;
        }
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setTableWidth(entry.contentRect.width);
            }
        });
        resizeObserver.observe(tableContainerRef.current);
        return () => {
            resizeObserver.disconnect();
        };
    }, []);
    const onSortingChange = sorting && setSorting
        ? (updater) => {
            const newValue = typeof updater === "function" ? updater(sorting) : updater;
            setSorting(newValue);
        }
        : undefined;
    const onRowSelectionChange = rowSelection && setRowSelection
        ? (updater) => {
            const newValue = typeof updater === "function" ? updater(rowSelection) : updater;
            setRowSelection(newValue);
        }
        : undefined;
    const table = useReactTable({
        data,
        columns,
        rowCount: totalRowCount,
        getCoreRowModel: getCoreRowModel(),
        enableColumnResizing: true,
        ...(enableRowSelection && {
            onRowSelectionChange,
        }),
        state: {
            ...(enableRowSelection && { rowSelection }),
            ...(isSorting && {
                sorting,
            }),
        },
        manualSorting: isSorting,
        ...(isSorting && {
            onSortingChange: onSortingChange,
        }),
        enableSortingRemoval: true,
        enableRowSelection,
        enableMultiRowSelection,
        getRowId,
    });
    useEffect(() => {
        if (!tableContainerRef.current || !table || !tableWidth) {
            return;
        }
        const columns = table.getAllColumns();
        // Calculate ideal widths and handle minimums
        const idealSizing = columns.reduce((acc, column) => {
            var _a;
            const ratio = ((_a = column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.sizeRatio) || 0;
            const calculated = Math.max(Math.floor((ratio / 100) * tableWidth), MIN_COLUMN_WIDTH);
            return { ...acc, [column.id]: calculated };
        }, {});
        // Ensure total width matches tableWidth
        const totalIdealWidth = Object.values(idealSizing).reduce((a, b) => a + b, 0);
        const widthDifference = tableWidth - totalIdealWidth;
        // adjust the largest column with leftover size
        if (widthDifference !== 0) {
            const adjustColumnId = Object.entries(idealSizing).sort((a, b) => b[1] - a[1])[0][0];
            idealSizing[adjustColumnId] += widthDifference;
        }
        table.setColumnSizing(idealSizing);
    }, [table, tableWidth]);
    // Get the current column sizing from the table for rendering
    const columnSizing = table.getState().columnSizing;
    const { rows } = table.getRowModel();
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => COLUMN_HEIGHT,
    });
    // Intersection observer for infinite loading
    useEffect(() => {
        if (!onLoadMore || !loadMoreRef.current) {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            // retrieving the sentinel div
            if (entries[0].isIntersecting && !isLoading) {
                onLoadMore();
            }
        }, {
            root: tableContainerRef.current,
            rootMargin: "200% 0% 0% 0%",
            threshold: 0.1,
        });
        observer.observe(loadMoreRef.current);
        return () => {
            if (loadMoreRef.current) {
                observer.unobserve(loadMoreRef.current);
            }
            observer.disconnect();
        };
    }, [onLoadMore, isLoading]);
    return (React.createElement("div", { className: cn("s-relative s-overflow-y-auto s-overflow-x-hidden", className, widthClassName, maxHeight === true
            ? "s-flex-1"
            : typeof maxHeight === "string"
                ? maxHeight
                : "s-max-h-100"), ref: setRef },
        React.createElement("div", { className: "s-relative" },
            React.createElement(DataTable.Root, { className: "s-w-full s-table-fixed" },
                React.createElement(DataTable.Header, { className: "s-sticky s-top-0 s-z-20 s-bg-white s-shadow-sm dark:s-bg-background-night" }, table.getHeaderGroups().map((headerGroup) => (React.createElement(DataTable.Row, { key: headerGroup.id, widthClassName: widthClassName }, headerGroup.headers.map((header) => {
                    const breakpoint = columnsBreakpoints[header.id];
                    if (!windowSize.width ||
                        !shouldRenderColumn(windowSize.width, breakpoint)) {
                        return null;
                    }
                    return (React.createElement(DataTable.Head, { column: header.column, key: header.id, onClick: isSorting && header.column.getCanSort()
                            ? header.column.getToggleSortingHandler()
                            : undefined, className: cn("s-max-w-0", header.column.getCanSort() &&
                            isSorting &&
                            "s-cursor-pointer"), style: {
                            width: columnSizing[header.id],
                            minWidth: columnSizing[header.id],
                        } },
                        React.createElement("div", { className: "s-flex s-w-full s-items-center s-space-x-1 s-whitespace-nowrap" },
                            React.createElement("span", { className: "s-truncate" }, flexRender(header.column.columnDef.header, header.getContext())),
                            isSorting && header.column.getCanSort() && (React.createElement(Icon, { visual: header.column.getIsSorted() === "asc"
                                    ? ArrowUpIcon
                                    : ArrowDownIcon, size: "xs", className: cn("s-ml-1", header.column.getIsSorted()
                                    ? "s-opacity-100"
                                    : "s-opacity-0") })))));
                }))))),
                React.createElement(DataTable.Body, { className: "s-relative s-w-full", style: {
                        height: `${rowVirtualizer.getTotalSize()}px`,
                    } }, rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const row = rows[virtualRow.index];
                    const handleRowClick = () => {
                        var _a, _b;
                        if (enableRowSelection && row.getCanSelect()) {
                            row.toggleSelected(!enableMultiRowSelection ? true : undefined);
                        }
                        (_b = (_a = row.original).onClick) === null || _b === void 0 ? void 0 : _b.call(_a);
                    };
                    return (React.createElement(DataTable.Row, { key: row.id, id: row.id, widthClassName: widthClassName, onClick: enableRowSelection ? handleRowClick : row.original.onClick, onDoubleClick: row.original.onDoubleClick, rowData: row.original, className: "s-absolute s-w-full", ...(enableRowSelection && {
                            "data-selected": row.getIsSelected(),
                        }), style: {
                            transform: `translateY(${virtualRow.start}px)`,
                        } }, row.getVisibleCells().map((cell) => {
                        const breakpoint = columnsBreakpoints[cell.column.id];
                        if (!windowSize.width ||
                            !shouldRenderColumn(windowSize.width, breakpoint)) {
                            return null;
                        }
                        return (React.createElement(DataTable.Cell, { column: cell.column, key: cell.id, id: cell.id, className: "s-max-w-0", style: {
                                width: columnSizing[cell.column.id],
                                minWidth: columnSizing[cell.column.id],
                            } },
                            React.createElement("div", { className: "s-flex s-items-center s-space-x-1" },
                                React.createElement("span", { className: "s-truncate" }, flexRender(cell.column.columnDef.cell, cell.getContext())))));
                    })));
                }))),
            React.createElement("div", { ref: loadMoreRef, className: "s-absolute s-bottom-0 s-h-1 s-w-full" })),
        isLoading && (React.createElement("div", { className: "s-sticky s-bottom-0 s-left-0 s-right-0 s-flex s-justify-center s-bg-white/80 s-py-2 s-backdrop-blur-sm dark:s-bg-background-night/80" },
            React.createElement("div", { className: "s-flex s-items-center s-gap-2 s-text-sm s-text-muted-foreground" },
                React.createElement(Spinner, { size: "xs" }),
                React.createElement("span", null, "Loading more data..."))))));
}
DataTable.Root = function DataTableRoot({ children, className, containerClassName, containerProps, ...props }) {
    return (React.createElement("div", { className: cn("s-@container/table", containerClassName), ...containerProps },
        React.createElement("table", { className: cn("s-w-full s-table-fixed s-border-collapse", className), ...props }, children)));
};
DataTable.Header = function Header({ children, className, ...props }) {
    return (React.createElement("thead", { className: cn(className), ...props }, children));
};
DataTable.Head = function Head({ children, className, column, ...props }) {
    var _a, _b;
    return (React.createElement("th", { className: cn("s-heading-xs s-py-2 s-pl-2 s-pr-3 s-text-left s-capitalize", "s-text-foreground dark:s-text-foreground-night", (_a = column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.className, className), ...props }, ((_b = column.columnDef.meta) === null || _b === void 0 ? void 0 : _b.tooltip) ? (React.createElement(Tooltip, { label: column.columnDef.meta.tooltip, trigger: children })) : (children)));
};
DataTable.Body = function Body({ children, className, ...props }) {
    return (React.createElement("tbody", { className: className, ...props }, children));
};
DataTable.Row = function Row({ children, className, onClick, onDoubleClick, widthClassName, rowData, ...props }) {
    var _a, _b;
    const [contextMenuPosition, setContextMenuPosition] = useState(null);
    const handleContextMenu = (event) => {
        var _a;
        if (!((_a = rowData === null || rowData === void 0 ? void 0 : rowData.menuItems) === null || _a === void 0 ? void 0 : _a.length)) {
            return;
        }
        event.preventDefault();
        setContextMenuPosition({ x: event.clientX, y: event.clientY });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("tr", { className: cn("s-group/dt-row s-justify-center s-border-b s-transition-colors s-duration-300 s-ease-out", "s-border-separator dark:s-border-separator-night", (onClick || onDoubleClick) &&
                "s-cursor-pointer [&:hover:not(:has(input:hover)):not(:has(button:hover))]:s-bg-muted dark:[&:hover:not(:has(input:hover)):not(:has(button:hover))]:s-bg-muted-night", props["data-selected"] && "s-bg-muted/50 dark:s-bg-muted/50", widthClassName, className), onClick: onClick || undefined, onDoubleClick: onDoubleClick || undefined, onContextMenu: handleContextMenu, ...props }, children),
        contextMenuPosition && ((_a = rowData === null || rowData === void 0 ? void 0 : rowData.menuItems) === null || _a === void 0 ? void 0 : _a.length) && (React.createElement(DropdownMenu, { open: !!contextMenuPosition, onOpenChange: (open) => !open && setContextMenuPosition(null), modal: true },
            React.createElement(DropdownMenuPortal, null,
                React.createElement(DropdownMenuContent, { align: "start", className: "s-whitespace-nowrap", style: {
                        position: "fixed",
                        left: (contextMenuPosition === null || contextMenuPosition === void 0 ? void 0 : contextMenuPosition.x) || 0,
                        top: (contextMenuPosition === null || contextMenuPosition === void 0 ? void 0 : contextMenuPosition.y) || 0,
                    } },
                    React.createElement(DropdownMenuGroup, null, (_b = rowData === null || rowData === void 0 ? void 0 : rowData.menuItems) === null || _b === void 0 ? void 0 : _b.map((item, index) => renderMenuItem(item, index, () => setContextMenuPosition(null))))))))));
};
// Shared menu rendering functions
const renderSubmenuItem = (item, index, onItemClick) => (React.createElement(DropdownMenuSub, { key: `${item.label}-${index}` },
    React.createElement(DropdownMenuSubTrigger, { label: item.label, disabled: item.disabled }),
    React.createElement(DropdownMenuPortal, null,
        React.createElement(DropdownMenuSubContent, null,
            React.createElement(ScrollArea, { className: "s-flex s-max-h-72 s-min-w-24 s-flex-col", hideScrollBar: true },
                item.items.map((subItem) => (React.createElement(DropdownMenuItem, { key: subItem.id, label: subItem.name, onClick: (event) => {
                        event.stopPropagation();
                        item.onSelect(subItem.id);
                        onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick();
                    } }))),
                React.createElement(ScrollBar, { className: "s-py-0" }))))));
const renderRegularItem = (item, index, onItemClick) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { kind, ...itemProps } = item;
    return (React.createElement(DropdownMenuItem, { key: `item-${index}`, ...itemProps, onClick: (event) => {
            var _a;
            event.stopPropagation();
            (_a = itemProps.onClick) === null || _a === void 0 ? void 0 : _a.call(itemProps, event);
            onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick();
        } }));
};
const renderMenuItem = (item, index, onItemClick) => {
    switch (item.kind) {
        case "submenu":
            return renderSubmenuItem(item, index, onItemClick);
        case "item":
            return renderRegularItem(item, index, onItemClick);
    }
};
DataTable.MoreButton = function MoreButton({ className, menuItems, dropdownMenuProps, disabled, }) {
    if (!(menuItems === null || menuItems === void 0 ? void 0 : menuItems.length)) {
        return null;
    }
    return (React.createElement(DropdownMenu, { modal: false, ...dropdownMenuProps },
        React.createElement(DropdownMenuTrigger, { onClick: (event) => {
                event.stopPropagation();
            }, asChild: true },
            React.createElement(Button, { icon: MoreIcon, size: "icon", variant: "ghost-secondary", disabled: disabled, className: cn(disabled && "s-cursor-not-allowed s-opacity-50", className) })),
        React.createElement(DropdownMenuContent, { align: "end", hidden: disabled },
            React.createElement(DropdownMenuGroup, null, menuItems.map((item, index) => renderMenuItem(item, index))))));
};
DataTable.Cell = function Cell({ children, className, column, ...props }) {
    var _a;
    return (React.createElement("td", { className: cn(cellHeight, "s-truncate s-pl-2", (_a = column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.className, className), ...props }, children));
};
DataTable.CellContent = function CellContent({ children, className, avatarUrl, avatarTooltipLabel, roundedAvatar, icon, iconClassName, description, grow = false, disabled, avatarStack, ...props }) {
    return (React.createElement("div", { className: cn("s-flex s-items-center", grow ? "s-flex-grow" : "", disabled && "s-cursor-not-allowed s-opacity-50", className), "aria-disabled": disabled || undefined, ...props },
        avatarUrl && avatarTooltipLabel && (React.createElement(Tooltip, { trigger: React.createElement(Avatar, { visual: avatarUrl, size: "xs", className: "s-mr-2", isRounded: roundedAvatar !== null && roundedAvatar !== void 0 ? roundedAvatar : false }), label: avatarTooltipLabel })),
        avatarUrl && !avatarTooltipLabel && (React.createElement(Avatar, { visual: avatarUrl, size: "xs", className: "s-mr-2", isRounded: roundedAvatar !== null && roundedAvatar !== void 0 ? roundedAvatar : false })),
        avatarStack && (React.createElement(Avatar.Stack, { avatars: avatarStack.items, nbVisibleItems: avatarStack.nbVisibleItems, size: "xs" })),
        icon && (React.createElement(Icon, { visual: icon, size: "sm", className: cn("s-mr-2 s-text-foreground dark:s-text-foreground-night", iconClassName) })),
        React.createElement("div", { className: cn("s-flex s-shrink s-truncate", grow ? "s-flex-grow" : "") },
            React.createElement("div", { className: cn(grow ? "s-flex-grow" : "", "s-truncate s-text-sm", "s-text-foreground dark:s-text-foreground-night") }, children),
            description && (React.createElement("span", { className: cn("s-pl-2 s-text-sm", "s-text-muted-foreground dark:s-text-muted-foreground-night") }, description)))));
};
DataTable.BasicCellContent = function BasicCellContent({ label, tooltip, className, textToCopy, disabled, ...props }) {
    const [isCopied, copyToClipboard] = useCopyToClipboard();
    const handleCopy = async () => {
        const textToUse = textToCopy !== null && textToCopy !== void 0 ? textToCopy : String(label);
        void copyToClipboard(new ClipboardItem({
            "text/plain": new Blob([String(textToUse)], {
                type: "text/plain",
            }),
        }));
    };
    return (React.createElement(React.Fragment, null, tooltip ? (React.createElement(Tooltip, { tooltipTriggerAsChild: true, trigger: React.createElement("div", { className: cn(cellHeight, "s-group s-flex s-items-center s-gap-2 s-text-sm", "s-text-muted-foreground dark:s-text-muted-foreground-night", disabled && "s-cursor-not-allowed s-opacity-50", className), "aria-disabled": disabled || undefined, ...props },
            React.createElement("span", { className: "s-truncate" }, label),
            textToCopy && (React.createElement(Button, { icon: isCopied ? ClipboardCheckIcon : ClipboardIcon, className: "s-hidden group-hover:s-block", variant: "outline", onClick: async (e) => {
                    e.stopPropagation();
                    await handleCopy();
                }, size: "xs" }))), label: tooltip })) : (React.createElement("div", { className: cn(cellHeight, "s-group s-flex s-items-center s-gap-2 s-text-sm", "s-text-muted-foreground dark:s-text-muted-foreground-night", disabled && "s-cursor-not-allowed s-opacity-50", className), "aria-disabled": disabled || undefined, ...props },
        React.createElement("span", { className: "s-truncate" }, label),
        textToCopy && (React.createElement(Button, { icon: isCopied ? ClipboardCheckIcon : ClipboardIcon, className: "s-hidden group-hover:s-block", variant: "outline", onClick: async (e) => {
                e.stopPropagation();
                await handleCopy();
            }, size: "xs" }))))));
};
DataTable.CellContentWithCopy = function CellContentWithCopy({ children, textToCopy, className, }) {
    const [isCopied, copyToClipboard] = useCopyToClipboard();
    const handleCopy = async () => {
        void copyToClipboard(new ClipboardItem({
            "text/plain": new Blob([textToCopy !== null && textToCopy !== void 0 ? textToCopy : String(children)], {
                type: "text/plain",
            }),
        }));
    };
    return (React.createElement("div", { className: cn("s-flex s-items-center s-space-x-2", className) },
        React.createElement("span", { className: "s-truncate" }, children),
        React.createElement(IconButton, { icon: isCopied ? ClipboardCheckIcon : ClipboardIcon, variant: "outline", onClick: async (e) => {
                e.stopPropagation();
                await handleCopy();
            }, size: "xs" })));
};
DataTable.Caption = function Caption({ children, className, ...props }) {
    return (React.createElement("caption", { className: className, ...props }, children));
};
export function createSelectionColumn() {
    return {
        id: "select",
        enableSorting: false,
        enableHiding: false,
        header: ({ table }) => (React.createElement(Checkbox, { size: "xs", checked: table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                    ? "partial"
                    : false, onCheckedChange: (state) => {
                if (state === "indeterminate") {
                    return;
                }
                table.toggleAllRowsSelected(state);
            } })),
        cell: ({ row }) => (React.createElement("div", { className: "s-flex s-h-full s-w-full s-items-center" },
            React.createElement(Checkbox, { size: "xs", checked: row.getIsSelected(), disabled: !row.getCanSelect(), onCheckedChange: (state) => {
                    if (state === "indeterminate") {
                        return;
                    }
                    row.toggleSelected(state);
                } }))),
        meta: {
            className: "s-w-10",
        },
    };
}
export function createRadioSelectionColumn() {
    return {
        id: "radio-select",
        enableSorting: false,
        enableHiding: false,
        header: () => null,
        cell: ({ row }) => (React.createElement("div", { className: "s-flex s-h-full s-w-full s-items-center" },
            React.createElement("div", { className: cn(radioStyles({ size: "xs" }), row.getIsSelected() && "s-bg-muted/50 dark:s-bg-muted/50", !row.getCanSelect() && "s-cursor-not-allowed s-opacity-50"), "aria-checked": row.getIsSelected(), role: "radio" }, row.getIsSelected() && (React.createElement("div", { className: radioIndicatorStyles({ size: "xs" }) }))))),
        meta: {
            className: "s-w-10",
        },
    };
}
//# sourceMappingURL=DataTable.js.map